import React from 'react';
import { Waves, Wind, ArrowUp, ArrowDown, Clock, Droplets, Thermometer, CloudSun, Camera, AlertTriangle, Zap, ExternalLink, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { WMO_CODES, DataSourceBadge, formatHour, getShapeLabel, formatTideTime } from './constants';
import SuitRecommendation from './SuitRecommendation';

export default function SpotOverviewTab({
  current, summary, hasWindy, hasOM,
  noaaLoaded, noaa, nextTide,
  alerts, hasAlerts,
  bestTime,
  mappedCams, webcams,
}) {
  return (
    <div className="space-y-4" data-testid="spot-overview">
      {/* NWS Beach Hazard Alerts */}
      {hasAlerts && (
        <div className="space-y-2" data-testid="spot-alerts">
          {alerts.map((alert, i) => {
            const sev = (alert.severity || '').toLowerCase();
            const colors = sev === 'extreme' || sev === 'severe'
              ? 'bg-red-500/15 border-red-500/30 text-red-300'
              : sev === 'moderate'
              ? 'bg-orange-500/15 border-orange-500/30 text-orange-300'
              : 'bg-yellow-500/15 border-yellow-500/30 text-yellow-300';
            return (
              <motion.div key={i} initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl border p-3.5 ${colors}`} data-testid={`alert-${i}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold">{alert.event}</p>
                    <p className="text-[10px] opacity-80 mt-0.5 line-clamp-2">{alert.headline}</p>
                    {alert.expires && (
                      <p className="text-[9px] opacity-50 mt-1">Expires: {new Date(alert.expires).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                    )}
                  </div>
                  <span className="text-[8px] uppercase tracking-wider opacity-60 flex-shrink-0">{alert.severity}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Best Time to Surf */}
      {bestTime && (
        <div className="bg-gradient-to-br from-primary/10 via-bg-subtle/80 to-emerald-500/5 border border-primary/20 rounded-2xl p-4" data-testid="best-time-to-surf">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              <p className="text-[10px] tracking-widest uppercase text-primary font-semibold">Best Time to Surf</p>
            </div>
            <span className={`text-[10px] px-2.5 py-0.5 rounded-xl font-bold ${
              bestTime.label === 'Epic' ? 'bg-primary/20 text-primary' :
              bestTime.label === 'Great' ? 'bg-green-400/20 text-green-400' :
              bestTime.label === 'Good' ? 'bg-lime-400/20 text-lime-400' :
              bestTime.label === 'Fair' ? 'bg-yellow-400/20 text-yellow-400' :
              'bg-orange-400/20 text-orange-400'
            }`} data-testid="best-time-label">{bestTime.label}</span>
          </div>
          <div className="flex items-center gap-4 mb-3">
            <div>
              <p className="text-2xl font-bold">{formatHour(bestTime.window_start)} - {formatHour(bestTime.window_end)}</p>
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">Optimal window today</p>
            </div>
            <div className="ml-auto text-right">
              <div className="flex items-center gap-1">
                <div className="w-20 h-2 bg-white/[0.05] rounded-full overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${bestTime.score}%` }}
                    className={`h-full rounded-full ${bestTime.score >= 70 ? 'bg-primary' : bestTime.score >= 50 ? 'bg-green-400' : bestTime.score >= 35 ? 'bg-yellow-400' : 'bg-orange-400'}`} />
                </div>
                <span className="text-xs font-bold">{Math.round(bestTime.score)}</span>
              </div>
              <p className="text-[9px] text-muted-foreground/40">score / {bestTime.max_score}</p>
            </div>
          </div>
          {bestTime.reasons && bestTime.reasons.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="best-time-reasons">
              {bestTime.reasons.map((r, i) => (
                <span key={i} className="text-[9px] px-2 py-0.5 rounded-full bg-white/[0.05] text-muted-foreground/70 border border-white/5">{r}</span>
              ))}
            </div>
          )}
          {bestTime.hours && bestTime.hours.length > 0 && (
            <div className="flex gap-2 mt-3">
              {bestTime.hours.map((h, i) => (
                <div key={i} className="flex-1 bg-white/[0.03] border border-white/5 rounded-xl p-2 text-center">
                  <p className="text-[10px] text-muted-foreground/50 font-mono">{formatHour(h.hour)}</p>
                  <p className="text-xs font-bold mt-0.5">{h.size_ft}<span className="text-[9px] text-muted-foreground/40">ft</span></p>
                  {h.shape !== null && h.shape !== undefined && <p className="text-[9px] text-muted-foreground/50">{getShapeLabel(h.shape).label}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(hasWindy || hasOM) && (
        <div className="bg-bg-subtle/80 border border-white/10 rounded-2xl p-4" data-testid="spot-current-conditions">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] tracking-widest uppercase text-muted-foreground">Current Conditions</p>
            {summary && <span className="text-[10px] text-primary border border-primary/30 rounded-xl px-2.5 py-0.5">{summary}</span>}
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center"><Waves className="w-4 h-4 text-primary mx-auto mb-1" /><p className="text-lg font-bold">{current.wave_height_ft || '-'}'</p><p className="text-[9px] text-muted-foreground">waves</p></div>
            <div className="text-center"><Clock className="w-4 h-4 text-primary mx-auto mb-1" /><p className="text-lg font-bold">{current.wave_period_s || current.swell_period_s || '-'}s</p><p className="text-[9px] text-muted-foreground">{current.wave_direction || ''} period</p></div>
            <div className="text-center"><Wind className="w-4 h-4 text-primary mx-auto mb-1" /><p className="text-lg font-bold">{current.wind_speed_kph || '-'}</p><p className="text-[9px] text-muted-foreground">{current.wind_direction || ''} kph</p></div>
          </div>
          <div className="grid grid-cols-4 gap-2 pt-2 border-t border-white/5">
            {current.swell_height_ft !== undefined && <div className="text-center"><ArrowUp className="w-3 h-3 text-secondary mx-auto mb-0.5" /><p className="text-xs font-bold">{current.swell_height_ft}'</p><p className="text-[8px] text-muted-foreground">{current.swell_direction} swell</p></div>}
            {current.air_temp_f && <div className="text-center"><Thermometer className="w-3 h-3 text-accent mx-auto mb-0.5" /><p className="text-xs font-bold">{Math.round(current.air_temp_f)}</p><p className="text-[8px] text-muted-foreground">air &deg;F</p></div>}
            {current.sea_surface_temp_c > 0 && <div className="text-center"><Droplets className="w-3 h-3 text-blue-400 mx-auto mb-0.5" /><p className="text-xs font-bold">{Math.round(current.sea_surface_temp_c * 9/5 + 32)}</p><p className="text-[8px] text-muted-foreground">water &deg;F</p></div>}
            {current.weather_code !== undefined && <div className="text-center"><CloudSun className="w-3 h-3 text-yellow-400 mx-auto mb-0.5" /><p className="text-[10px] font-medium">{WMO_CODES[current.weather_code] || 'N/A'}</p><p className="text-[8px] text-muted-foreground">sky</p></div>}
          </div>
        </div>
      )}

      {/* Suit Recommendation */}
      {current && (() => {
        const wt = noaa?.water_temp?.temp_f || (current.sea_surface_temp_c > 0 ? Math.round(current.sea_surface_temp_c * 9/5 + 32) : 0);
        return wt > 0 ? <SuitRecommendation waterTempF={wt} /> : null;
      })()}

      {/* Quick Tide summary */}
      {noaaLoaded && nextTide && (
        <div className="bg-bg-subtle/80 border border-white/10 rounded-2xl p-4" data-testid="overview-tide-summary">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] tracking-widest uppercase text-muted-foreground">Tides</p>
            <span className="text-[9px] text-blue-400/60">{noaa.station?.name} ({noaa.station?.distance_km}km)</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5 text-center">
              {nextTide.type === 'high' ? <ArrowUp className="w-3.5 h-3.5 text-blue-400 mx-auto mb-1" /> : <ArrowDown className="w-3.5 h-3.5 text-cyan-400 mx-auto mb-1" />}
              <p className="text-sm font-bold">{nextTide.height_ft.toFixed(1)}<span className="text-[9px] text-muted-foreground/50">ft</span></p>
              <p className="text-[9px] text-muted-foreground/40">next {nextTide.type}</p>
            </div>
            {noaa.water_level && (
              <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5 text-center">
                <Droplets className="w-3.5 h-3.5 text-blue-400 mx-auto mb-1" />
                <p className="text-sm font-bold">{noaa.water_level.level_ft.toFixed(1)}<span className="text-[9px] text-muted-foreground/50">ft</span></p>
                <p className="text-[9px] text-muted-foreground/40">water level</p>
              </div>
            )}
            {noaa.water_temp && (
              <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5 text-center">
                <Thermometer className="w-3.5 h-3.5 text-cyan-400 mx-auto mb-1" />
                <p className="text-sm font-bold">{noaa.water_temp.temp_f}<span className="text-[9px] text-muted-foreground/50">&deg;F</span></p>
                <p className="text-[9px] text-muted-foreground/40">water temp</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mapped Cams (Live Look) */}
      {mappedCams.length > 0 && (
        <div data-testid="mapped-cams">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-[10px] tracking-widest uppercase text-muted-foreground">Live Look</p>
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 animate-pulse">LIVE</span>
          </div>
          <div className="space-y-2">
            {mappedCams.map((cam, i) => (
              <div key={cam.id || i} className="bg-bg-subtle/60 border border-primary/20 rounded-2xl overflow-hidden group">
                <div className="relative">
                  {cam.thumbnail ? (
                    <img src={cam.thumbnail} alt={cam.title} className="w-full aspect-video object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full aspect-video bg-black/20 flex items-center justify-center">
                      <Camera className="w-8 h-8 text-muted-foreground/20" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <span className="text-[8px] px-1.5 py-0.5 rounded bg-primary/80 text-white font-bold">SPOT CAM</span>
                  </div>
                  {cam.status === 'active' && (
                    <div className="absolute top-2 right-2">
                      <span className="text-[8px] px-1.5 py-0.5 rounded bg-green-500/80 text-white font-medium flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                      </span>
                    </div>
                  )}
                  {cam.player_day && (
                    <a href={cam.player_day} target="_blank" rel="noopener noreferrer"
                      className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`mapped-cam-play-${i}`}>
                      <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center shadow-lg">
                        <Play className="w-5 h-5 text-white ml-0.5" />
                      </div>
                    </a>
                  )}
                </div>
                <div className="p-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-medium truncate">{cam.title}</p>
                    {cam.city && <p className="text-[9px] text-muted-foreground/50">{cam.city}</p>}
                  </div>
                  {cam.player_day && (
                    <a href={cam.player_day} target="_blank" rel="noopener noreferrer"
                      className="text-[9px] text-primary flex items-center gap-1 hover:underline flex-shrink-0" data-testid={`cam-external-${i}`}>
                      Watch <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nearby Cams */}
      {webcams.length > 0 && (
        <div>
          <p className="text-[10px] tracking-widest uppercase text-muted-foreground mb-2">Nearby Cams</p>
          <div className="grid grid-cols-2 gap-2">
            {webcams.map((cam, i) => (
              <div key={cam.id || i} className="bg-bg-subtle/50 border border-white/5 rounded-xl overflow-hidden">
                {cam.thumbnail ? <img src={cam.thumbnail} alt={cam.title} className="w-full aspect-video object-cover" loading="lazy" /> : <div className="w-full aspect-video bg-black/20 flex items-center justify-center"><Camera className="w-6 h-6 text-muted-foreground/20" /></div>}
                <div className="p-2 flex items-center justify-between">
                  <p className="text-[9px] text-muted-foreground truncate">{cam.title}</p>
                  {cam.player_day && (
                    <a href={cam.player_day} target="_blank" rel="noopener noreferrer" className="text-[9px] text-primary/60 hover:text-primary flex-shrink-0 ml-1">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
